import type { FormFieldV2, FormSettingsV2, CubbyType } from './form'
import type { BlockType, LinkPageTheme } from './linkPage'
import type { SupportedLocale } from '@/i18n'
import type { TemplateLandingLocaleContent } from '@/features/templates/landing/configs/types'

// --- Categories ---

export type LinkPageTemplateCategory =
  | 'business'
  | 'creative'
  | 'health_wellness'
  | 'events'
  | 'tech_product'
  | 'education'
  | 'community'
  | 'healthcare'
  | 'personal_brand'

export const LINK_PAGE_TEMPLATE_CATEGORIES: LinkPageTemplateCategory[] = [
  'business',
  'creative',
  'health_wellness',
  'events',
  'tech_product',
  'education',
  'community',
  'healthcare',
  'personal_brand',
]

export const LINK_PAGE_TEMPLATE_CATEGORY_META: Record<
  LinkPageTemplateCategory,
  { label: string; icon: string; description: string }
> = {
  business: { label: 'Business & Services', icon: 'Building2', description: 'Restaurants, real estate, consulting, home services' },
  creative: { label: 'Creative & Portfolio', icon: 'Palette', description: 'Photographers, musicians, freelancers' },
  health_wellness: { label: 'Health & Wellness', icon: 'Heart', description: 'Gyms, yoga, salons, spas' },
  events: { label: 'Events', icon: 'Calendar', description: 'Conferences, workshops, weddings' },
  tech_product: { label: 'Tech & Product', icon: 'Rocket', description: 'SaaS, product launches, apps' },
  education: { label: 'Education', icon: 'GraduationCap', description: 'Courses, coaching, tutoring' },
  community: { label: 'Community & Content', icon: 'Globe', description: 'Non-profits, podcasts, creators' },
  healthcare: { label: 'Healthcare', icon: 'Stethoscope', description: 'Medical practices, therapists' },
  personal_brand: { label: 'Personal Brand', icon: 'Sparkles', description: 'Link-in-bio, media kits, resumes' },
}

// --- Blueprint types ---

export interface TemplateBlueprintBlock {
  blockType: BlockType
  content: Record<string, unknown>
  position: number
  visible: boolean
  formRef?: string
}

export interface TemplateBlueprintForm {
  key: string
  name: string
  description?: string
  fields: FormFieldV2[]
  settings?: Partial<FormSettingsV2>
  cubbyType?: CubbyType
}

export interface TemplateBlueprintThemeVariant {
  name: string
  theme: LinkPageTheme
}

export interface LinkPageTemplateBlueprint {
  pageDefaults: { title: string; bio: string }
  blocks: TemplateBlueprintBlock[]
  forms: TemplateBlueprintForm[]
  themeVariants: TemplateBlueprintThemeVariant[]
}

// --- Template record ---

export type TemplateVisibility = 'public' | 'personal'
export type LinkPageTemplateSortOption = 'popular' | 'newest' | 'name'

export interface LinkPageTemplate {
  id: number
  name: string
  description: string
  category: LinkPageTemplateCategory
  slug: string
  previewImageUrl?: string
  usageCount: number
  visibility: TemplateVisibility
  system: boolean
  /** Needs advanced fields — a Free account is refused at use time. */
  premium?: boolean
  /** Cheapest plan slug that unlocks it. null when anyone can use it. */
  requiredPlan?: string | null
  accountId?: number
  blueprint: LinkPageTemplateBlueprint
  // Public landing fields — populated only by the unauthenticated
  // /api/public/link_page_templates/:slug endpoint.
  locale?: SupportedLocale
  availableLocales?: SupportedLocale[]
  marketingContent?: TemplateLandingLocaleContent | null
  createdAt: string
  updatedAt: string
}

// --- API types (snake_case) ---

export interface LinkPageTemplateFromAPI {
  id: number
  name: string
  description: string
  category: LinkPageTemplateCategory
  slug: string
  preview_image_url?: string
  usage_count: number
  visibility: TemplateVisibility
  system: boolean
  premium?: boolean
  /** Cheapest plan slug that unlocks it. null when anyone can use it. */
  required_plan?: string | null
  account_id?: number
  locale?: string
  available_locales?: string[]
  marketing_content?: TemplateLandingLocaleContent | null
  blueprint: {
    page_defaults: { title: string; bio: string }
    blocks: {
      block_type: BlockType
      content: Record<string, unknown>
      position: number
      visible: boolean
      form_ref?: string
    }[]
    forms: {
      key: string
      name: string
      description?: string
      fields: FormFieldV2[]
      settings?: Partial<FormSettingsV2>
      cubby_type?: CubbyType
    }[]
    theme_variants: {
      name: string
      theme: LinkPageTheme
    }[]
  }
  created_at: string
  updated_at: string
}

export interface UseLinkPageTemplateResult {
  id: number
  slug: string
  status: string
  createdAt: string
}

export interface UseLinkPageTemplateResultFromAPI {
  id: number
  slug: string
  status: string
  created_at: string
}

// --- Filters ---

export interface LinkPageTemplateFilters {
  category?: LinkPageTemplateCategory
  search?: string
  visibility?: TemplateVisibility
  sort?: LinkPageTemplateSortOption
  page?: number
  perPage?: number
}

// --- Category colors (for UI) ---

export const LINK_PAGE_TEMPLATE_CATEGORY_COLORS: Record<LinkPageTemplateCategory, string> = {
  business: 'bg-blue-100 text-blue-700',
  creative: 'bg-purple-100 text-purple-700',
  health_wellness: 'bg-green-100 text-green-700',
  events: 'bg-orange-100 text-orange-700',
  tech_product: 'bg-cyan-100 text-cyan-700',
  education: 'bg-indigo-100 text-indigo-700',
  community: 'bg-emerald-100 text-emerald-700',
  healthcare: 'bg-teal-100 text-teal-700',
  personal_brand: 'bg-pink-100 text-pink-700',
}
